import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "./ui/sonner";
import { Button } from "./ui/button";
import { ChevronDown, ChevronRight, Loader2, Star } from "lucide-react";
import { fmtD } from "../lib/date";

import { T } from "../lib/i18n";

const money = (n) => "$" + Number(n || 0).toLocaleString();

const StarRow = ({ sid, value, onPick, disabled }) => (
  <span className="inline-flex items-center gap-0.5" data-testid={`sub-rate-stars-${sid}`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={disabled} data-testid={`sub-rate-star-${sid}-${n}`} onClick={() => onPick(n)} className={`transition-transform hover:scale-110 ${disabled ? "cursor-default" : ""}`}>
        <Star className={`h-5 w-5 ${n <= (value || 0) ? "fill-amber-400 text-amber-500" : "text-slate-300"}`} />
      </button>
    ))}
  </span>
);

const RatingRow = ({ api, it, zh, reload, highlight }) => {
  const [score, setScore] = useState(it.rating?.score || 0);
  const [comment, setComment] = useState(it.rating?.comment || "");
  const [edit, setEdit] = useState(!it.rating);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => { if (highlight && ref.current) ref.current.scrollIntoView({ behavior: "smooth", block: "center" }); }, [highlight]);
  const save = async () => {
    if (!score) { toast.error(zh ? "请先点星评分" : "Pick a star rating"); return; }
    setBusy(true);
    try { await api.post(`/subcontracts/${it.subcontractId}/rating`, { score, comment }); toast.success(zh ? "评分已保存，将用于后续自动选商" : "Saved"); setEdit(false); reload(); } catch (e) { toast.error(e?.detailText || T("失败")); }
    setBusy(false);
  };
  return (
    <div ref={ref} data-testid={`sub-rate-row-${it.subcontractId}`} data-rated={it.rating ? "1" : "0"} className={`rounded-xl border p-2.5 text-xs space-y-1.5 ${highlight ? "border-amber-400 ring-2 ring-amber-200 bg-amber-50/40" : it.rating ? "border-slate-200 bg-white" : "border-amber-200 bg-amber-50/30"}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-slate-800">{it.tradeName} · {it.subcontractorName}</span>
        <span className="text-slate-500">{zh ? "已付" : "Paid"} <b className="text-emerald-700">{money(it.paidAmount)}</b>{it.contractAmount ? ` / ${money(it.contractAmount)}` : ""}{it.paidCount ? ` · ${it.paidCount} ${zh ? "笔" : "pmts"}` : ""}</span>
        {it.company?.pmRatingCount > 0 && <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5" data-testid={`sub-rate-company-${it.subcontractId}`}>{zh ? "该团队均分" : "Team avg"} {Number(it.company.pmRating).toFixed(1)}★ ×{it.company.pmRatingCount}</span>}
        {!it.rating && it.canRate && <span className="text-[10px] font-semibold text-amber-700">{zh ? "待评价" : "Pending"}</span>}
      </div>
      {it.rating && !edit ? (
        <div className="flex items-center gap-2 flex-wrap" data-testid={`sub-rate-current-${it.subcontractId}`}>
          <StarRow sid={it.subcontractId} value={it.rating.score} onPick={() => {}} disabled />
          <span className="text-slate-600">{it.rating.comment ? `“${T(it.rating.comment)}”` : ""} <span className="text-slate-400">· {it.rating.by} · {fmtD(it.rating.at)}{(it.rating.history || []).length > 1 ? (zh ? ` · 已修改 ${it.rating.history.length - 1} 次` : ` · edited`) : ""}</span></span>
          {it.canRate && <button data-testid={`sub-rate-edit-${it.subcontractId}`} onClick={() => setEdit(true)} className="text-teal-700 underline">{zh ? "修改" : "Edit"}</button>}
        </div>
      ) : it.canRate ? (
        <div className="flex items-center gap-2 flex-wrap">
          <StarRow sid={it.subcontractId} value={score} onPick={setScore} />
          <input data-testid={`sub-rate-comment-${it.subcontractId}`} value={comment} onChange={(e) => setComment(e.target.value.slice(0, 300))} placeholder={zh ? "一句评语（质量 / 进度 / 配合）" : "One-line comment"} className="h-7 flex-1 min-w-[180px] rounded border border-slate-300 px-2" />
          <Button size="sm" data-testid={`sub-rate-save-${it.subcontractId}`} disabled={busy} onClick={save} className="h-7 text-[11px] bg-amber-600 hover:bg-amber-700">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : (zh ? "提交评分" : "Submit")}</Button>
          {it.rating && <button onClick={() => setEdit(false)} className="text-slate-400 underline">{zh ? "取消" : "Cancel"}</button>}
        </div>
      ) : <p className="text-slate-400">{zh ? "尚未评分（由项目经理在付款完成后评价）" : "Not rated yet"}</p>}
    </div>
  );
};

/* 项目指挥中心：付款完成后 PM 评价分包（1–5 星 + 评语）→ 回流公司评分 → 自动选商优先 */
export const SubRatingPanel = ({ api, id, zh, refreshTick, focusId }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const load = useCallback(() => api.get(`/projects/${id}/sub-ratings`).then(({ data: d }) => setData(d)).catch(() => setData(null)), [api, id]);
  useEffect(() => { load(); }, [load, refreshTick]);
  useEffect(() => { if (focusId) setOpen(true); }, [focusId]);
  if (!data) return null;
  const items = data.items.filter((x) => x.paidAmount > 0 || x.rating);
  if (items.length === 0) return null;
  return (
    <div className="bg-white rounded-2xl border border-amber-100 shadow-sm" data-testid="hub-sub-rating" data-pending={data.pending}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-4 py-3 text-left" data-testid="hub-sub-rating-toggle">
        {open ? <ChevronDown className="h-4 w-4 text-amber-600" /> : <ChevronRight className="h-4 w-4 text-amber-600" />}
        <Star className="h-4 w-4 text-amber-500 fill-amber-400" />
        <span className="font-bold text-slate-800 text-sm">{zh ? "分包评价（付款完成后）" : "Subcontractor ratings"}</span>
        {data.pending > 0 && data.canRate && <span data-testid="hub-sub-rating-pending" className="text-[10px] font-semibold text-white bg-amber-500 rounded-full px-2 py-0.5">{zh ? `${data.pending} 项待评价` : `${data.pending} pending`}</span>}
        <span className="text-[11px] text-slate-400 ml-auto">{zh ? "评分回流分包商库，后续报价邀请优先高分团队" : "Feeds auto-selection of future RFQs"}</span>
      </button>
      {open && <div className="px-4 pb-4 space-y-2">{items.map((it) => <RatingRow key={it.subcontractId} api={api} it={it} zh={zh} reload={load} highlight={focusId === it.subcontractId} />)}</div>}
    </div>
  );
};
